import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { editMyTask, editTaskStatus, editTheTask, getTheTask } from '@/_redux/actions/ActionTasks';
import { setConferenceData, startConference } from '@/_redux/actions/ActionConference';
import PreviewEventModal from '@/components/Admin/Tasks/PreviewEventModal';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';

const Component = React.memo(({ getTheTask, taskID, onClose = () => {}, setIsBusy = () => {} }) => {
    const location = useLocation();
    const [taskData, setTaskData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const getTaskIdFromURL = () => {
        const searchParams = new URLSearchParams(location.search);
        return searchParams.get('task');
    };

    const fetchTaskData = async (taskId) => {
        if (!taskId) return;

        setLoading(true);
        setError(null);
        setIsBusy(true);
        try {
            const task = await getTheTask(taskId);
            setTaskData(task);
            setShowModal(true);
        } catch (err) {
            console.error('Failed to fetch task:', err);
            setError('Failed to load event data');
            setShowModal(false);
        } finally {
            setLoading(false);
            setIsBusy(false);
        }
    };

    const handleUpdateTask = async (taskId, updatedFields = null) => {
        if (updatedFields) {
            setTaskData((prev) => (prev ? { ...prev, ...updatedFields } : prev));
        }
        await fetchTaskData(taskId);
    };

    useEffect(() => {
        const taskId = getTaskIdFromURL() || taskID;
        if (taskId) {
            fetchTaskData(taskId);
        } else {
            setShowModal(false);
            setTaskData(null);
        }
    }, [location.search, location.pathname, taskID]);

    // Close handler
    const handleClose = () => {
        setShowModal(false);
        setTaskData(null);
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete('task');
        const newSearch = searchParams.toString();
        const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
        window.history.replaceState({}, '', newUrl);
        onClose();
    };

    if (loading && !taskID) {
        return (
            <div className="d-flex justify-content-center p-4">
                <div className="spinner-border" role="status">
                    <span className="sr-only">Loading event...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger m-3" role="alert">
                {error}
            </div>
        );
    }

    return (
        <>
            {showModal && taskData && (
                <PreviewEventModal data={taskData} handleClose={handleClose} handleUpdateTask={handleUpdateTask} />
            )}
        </>
    );
});

const mapStateToProps = (state) => ({
    currUser: state.user.user,
});

const EventModalFromURL = (props) => (
    <ComponentErrorBoundary componentErrorMessage={props?.componentErrorMessage}>
        <Component {...props} />
    </ComponentErrorBoundary>
);

export default connect(mapStateToProps, {
    editTheTask,
    editMyTask,
    editTaskStatus,
    startConference,
    setConferenceData,
    getTheTask,
})(EventModalFromURL);
